import {
  CDN_ASSETS,
  isCaponeTenant,
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import {
  B2BLoadingPopup,
  Header,
  HotelSplitMapView,
  mapTopAmenitiesToAmenities,
  useDeviceTypes,
  WishlistHeaderEntry,
  wishlistManagementModalActions,
  WishlistFtuxPopover,
} from "halifax";
import React, { useContext, useEffect, useMemo } from "react";
import { RouterProps } from "react-router";
import {
  APPLIED_PREFERENCES,
  AppliedPreferencesProperties,
  IIdLodgings,
} from "redmond";

import clsx from "clsx";
import dayjs from "dayjs";

import { STAYS_WISHLIST, STAYS_WISHLIST_FTUX } from "@capone/experiments";
import { useDispatch } from "react-redux";
import { config } from "../../api/config";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../App";
import {
  addTrackingProperties,
  AVAILABILITY_CLUSTER_MAP,
  AVAILABLE,
  CLUSTER_MAP_VARIANTS,
  CONTROL,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  STAYS_SEARCH,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { usePrevious } from "../../hooks/usePrevious";
import {
  PORTAL_TITLE,
  SELECT_HOTEL_TITLE,
  SELECT_STAYS_TITLE,
} from "../../lang/textConstants";
import { PATH_HOME, PATH_HOME_STAYS } from "../../utils/paths";
import { RewardsAccountSelection } from "../rewards/components";
import {
  parseQueryFromString,
  parseQueryString,
} from "../search/utils/parseQueryString";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { ConnectedWishlistManagementModal } from "../wishlist/components/WishlistManagementModal/container";
import { WishlistSnackbar } from "../wishlist/components/WishlistSnackbar/component";
import {
  AvailabilityErrorModal,
  AvailabilityList,
  AvailabilityMap,
  MobileHotelAvailability,
} from "./components";
import { ConnectedAvailabilityClusterMap } from "./components/AvailabilityClusterMap/container";
import { getStayTypeFilterConfig } from "./components/utilities";
import { HotelAvailabilityConnectorProps } from "./container";
import { HotelAvailabilityCallState } from "./reducer/state";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IHotelAvailability
  extends HotelAvailabilityConnectorProps,
    RouterProps {}

export const HotelAvailability = (props: IHotelAvailability) => {
  const {
    location,
    fromDate,
    untilDate,
    hotelAvailabilityCallState,
    fetchInitialHotelAvailability,
    history,
    fetchTravelWalletDetails,
    largestValueAccount,
    fetchTravelWalletCreditHistory,
    isSearchingMap,
    shouldApplyUserHotelPreferences,
    userHotelPreferences,
    setStarRatingsFilter,
    setAmenitiesFilter,
    setFreeCancelFilter,
    fetchUserHotelPreferences,
    listPaymentMethods,
    availabilityResultsHasHomesAndHotels,
    stayTypes,
    setHasDedicatedStayTypesFilter,
    setIsTravelApp,
    hasSeenWishlistFtux,
    fetchHasSeenWishlistFtux,
    updateHasSeenWishlistFtux,
    fetchHasSeenStaysFilterFtux,
  } = props;

  const { logo } = useContext(ClientContext);
  const dispatch = useDispatch();
  const { matchesMobile } = useDeviceTypes();
  const [locationName, setLocationName] = React.useState("");
  const [isSearchTermLodging, setIsSearchTermLodging] = React.useState(false);
  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [isSearchTermPoint, setIsSearchTermPoint] = React.useState(false);
  const [mobileMapOpen, setMobileMapOpen] = React.useState(false);

  const [currentUrl, setCurrentUrl] = React.useState(history.location.search);
  React.useEffect(() => {
    if (location) {
      const [locationName] = location?.label ? location.label.split(",") : [];
      setLocationName(locationName);
      const placeTypes = location
        ? (location.id as IIdLodgings).lodgingSelection.placeTypes
        : [];

      setIsSearchTermLodging(placeTypes?.includes("lodging"));
      setIsSearchTermPoint(
        !placeTypes?.includes("locality") &&
          !placeTypes?.includes("political") &&
          !placeTypes?.includes("country")
      );
    }
  }, [location]);

  const isMapSearch = useMemo(() => isSearchingMap, [isSearchingMap]);

  const expState = useExperiments();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );

  const mapVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP,
    CLUSTER_MAP_VARIANTS
  );

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isWishlistFtuxExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST_FTUX) ===
    AVAILABLE;

  const wishlistHeaderEntryIconRef = React.useRef<HTMLDivElement>(null);

  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const showEarnEnhancement =
    isCaponeTenant(config.TENANT) &&
    !!largestValueAccount &&
    !!largestValueAccount.earn.hotelsMultiplier;

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const customerProfileExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CUSTOMER_PROFILE_EXPERIMENT,
    CUSTOMER_PROFILE_VARIANTS
  );

  const isCustomerProfileExperiment =
    customerProfileExperimentVariant !== CONTROL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  useEffect(() => {
    const title = staysSearchEnabled ? SELECT_STAYS_TITLE : SELECT_HOTEL_TITLE;
    // TODO: Find out why window.scrollTo requires a setTimeout; it's needed so that the browser nav button works with scrollTo
    document.title = title;
    setTimeout(() => window.scrollTo(0, 0), 0);
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [staysSearchEnabled]);

  useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
  }, []);

  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const { data: { wishlists = [] } = {} } = useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  const hasWishlists = wishlists?.length > 0;

  useEffect(() => {
    if (isWishlistFtuxExperiment) {
      fetchHasSeenWishlistFtux();
    }
  }, [isWishlistFtuxExperiment]);

  useEffect(() => {
    if (staysSearchEnabled) {
      fetchHasSeenStaysFilterFtux();
    }
  }, [staysSearchEnabled]);

  useEffect(() => {
    if (expState.experiments.length > 0) {
      let shouldIncludeHomes = false;
      if (staysSearchEnabled) {
        shouldIncludeHomes = true;
      }

      fetchInitialHotelAvailability({
        history,
        searchFromMap: false,
        searchHotelsNear: false,
        includeHomes: shouldIncludeHomes,
      });
    }
  }, [expState]);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search]
  );
  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    if (history.location.search !== currentUrl) {
      const currShowMap = parsedQueryString?.showMap;
      const prevShowMap = parseQueryFromString(currentUrl).showMap;

      let shouldIncludeHomes = false;
      if (staysSearchEnabled) {
        shouldIncludeHomes = true;
      }
      if (!isTravelApp || prevShowMap === currShowMap) {
        fetchInitialHotelAvailability({
          history,
          searchFromMap: false,
          searchHotelsNear: false,
          includeHomes: shouldIncludeHomes,
        });
        setCurrentUrl(history.location.search);
      }
    }
  }, [history.location.search]);

  useEffect(() => {
    setIsTravelApp(isTravelApp);
  }, [isTravelApp]);

  useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  useEffect(() => {
    if (isCustomerProfileExperiment) {
      fetchUserHotelPreferences();
    }
  }, [isCustomerProfileExperiment]);

  useEffect(() => {
    if (isCustomerProfileExperiment && shouldApplyUserHotelPreferences) {
      userHotelPreferences?.starRatings.length &&
        setStarRatingsFilter(userHotelPreferences.starRatings);
      userHotelPreferences?.amenities.length &&
        setAmenitiesFilter(
          mapTopAmenitiesToAmenities(userHotelPreferences.amenities)
        );
      userHotelPreferences?.freeCancellationOnly !== undefined &&
        setFreeCancelFilter(userHotelPreferences.freeCancellationOnly);
    }
  }, [
    isCustomerProfileExperiment,
    shouldApplyUserHotelPreferences,
    userHotelPreferences,
  ]);

  const prevShouldApplyUserHotelPreferences = usePrevious(
    shouldApplyUserHotelPreferences
  );

  useEffect(() => {
    if (
      prevShouldApplyUserHotelPreferences === false &&
      shouldApplyUserHotelPreferences === true
    ) {
      const properties: AppliedPreferencesProperties = {
        preferences_type: "hotels",
        location: "hotel_list",
      };

      trackEvent({
        eventName: APPLIED_PREFERENCES,
        properties: addTrackingProperties(
          expState.trackingProperties,
          properties
        ),
      });
    }
  }, [shouldApplyUserHotelPreferences, prevShouldApplyUserHotelPreferences]);

  const { dedicatedStayTypeFilterEnabled } = getStayTypeFilterConfig({
    experiments: expState.experiments,
    availabilityResultsHasHomesAndHotels,
    stayTypes,
  });

  useEffect(() => {
    setHasDedicatedStayTypesFilter(dedicatedStayTypeFilterEnabled);
  }, [dedicatedStayTypeFilterEnabled]);

  const renderDesktopView = () => (
    <HotelSplitMapView
      className={clsx("hotel-availability-container", config.TENANT)}
      header={
        <Header
          className="rewards-components-section"
          left={
            <Box className="rewards-account-section-left-content">
              <Box
                className="logo"
                onClick={() =>
                  history.push(staysSearchEnabled ? PATH_HOME_STAYS : PATH_HOME)
                }
              >
                {logo}
              </Box>
              <Box className="rewards-account-section-travel-details">
                <Typography variant="body1" tabIndex={0}>
                  {constants.VIEWING_TEXT(
                    locationName,
                    isSearchTermLodging,
                    isSearchTermPoint,
                    isMapSearch,
                    staysSearchEnabled
                  )}
                </Typography>
                {fromDate && untilDate ? (
                  <Typography variant="body2">
                    {constants.DATES_TEXT(
                      dayjs(fromDate).format("ddd, MMM DD"),
                      dayjs(untilDate).format("ddd, MMM DD")
                    )}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          }
          right={
            <Box className="desktop-hotel-availability-rewards-account-contents">
              <RewardsAccountSelection
                className={clsx("b2b", {
                  "hide-balance-border": isTravelWalletCreditsExperiment,
                })}
                popoverClassName="b2b"
              />

              {isWishlistExperiment && (
                <>
                  <WishlistHeaderEntry
                    ref={wishlistHeaderEntryIconRef}
                    hasWishlists={hasWishlists}
                    onClick={() =>
                      dispatch(wishlistManagementModalActions.openModal({}))
                    }
                  />
                  {isWishlistFtuxExperiment &&
                    hotelAvailabilityCallState !==
                      HotelAvailabilityCallState.InitialSearchCallInProcess && (
                      <WishlistFtuxPopover
                        open={hasSeenWishlistFtux === false}
                        anchorRef={wishlistHeaderEntryIconRef}
                        onClose={() => {
                          updateHasSeenWishlistFtux(true);
                        }}
                      />
                    )}
                </>
              )}

              {isTravelWalletCreditsExperiment && <TravelWalletDrawer />}
            </Box>
          }
        />
      }
      leftClassName={clsx("list-section")}
      left={<AvailabilityList />}
      rightClassName={clsx("map-section")}
      right={
        mapVariantExp === CONTROL ? (
          <AvailabilityMap />
        ) : (
          <ConnectedAvailabilityClusterMap />
        )
      }
    />
  );

  const renderMobileView = () => (
    <Box
      className={clsx("hotel-availability-container", config.TENANT, {
        "mobile-map": mobileMapOpen,
      })}
    >
      <MobileHotelAvailability onShowMap={setMobileMapOpen} />
    </Box>
  );

  const capOneLoadingSecondaryText = showEarnEnhancement
    ? constants.EARN_ENHANCEMENT_SUBTITLE(
        largestValueAccount.earn.hotelsMultiplier,
        staysSearchEnabled ? largestValueAccount.earn.homesMultiplier : "",
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency
      )
    : largestValueAccount?.productDisplayName.toLowerCase().includes("paradise")
    ? constants.PARADISE_SUBTITLE
    : constants.fetchingAvailabilitySecondaryText(staysSearchEnabled);

  const isCorporate = isCorpTenant(config.TENANT);

  const loadingSecondaryMessage = isCorporate
    ? constants.CORP_FETCHING_AVAILABILITY_SECONDARY_TEXT
    : capOneLoadingSecondaryText;

  return (
    <>
      <B2BLoadingPopup
        open={
          hotelAvailabilityCallState ===
            HotelAvailabilityCallState.InitialSearchCallInProcess ||
          (matchesMobile &&
            hotelAvailabilityCallState ==
              HotelAvailabilityCallState.InitialMapSearchCallInProcess)
        }
        message={constants.fetchingAvailabilityText(staysSearchEnabled)}
        secondaryMessage={loadingSecondaryMessage}
        image={
          isCorporate
            ? CDN_ASSETS.caponeCorporate.searchLoading.hotels
            : CDN_ASSETS.capone.searchLoading.hotels
        }
        className="hotel-search-loading-popup"
        popupSize={matchesMobile ? "mobile" : "desktop"}
        onClose={() => {
          travelAppInterface.sendEvent({
            name: TravelAppInterfaceEventName.SEARCH_LOADED,
            funnel: TravelAppInterfaceFunnelType.HOTELS,
          });
        }}
        isXmod={isXmodRedesign}
      />

      <Box
        className={clsx("hotel-availability-root", {
          "full-width": !matchesMobile,
          "travel-app": isTravelApp,
        })}
      >
        {matchesMobile ? renderMobileView() : renderDesktopView()}
      </Box>

      <ConnectedWishlistManagementModal
        history={history}
        entryType="stays_availability"
      />
      <WishlistSnackbar />
      <AvailabilityErrorModal />
    </>
  );
};
